import { useEffect, useState } from 'react'

import { fetchPlatformStatus, PLATFORM_STATUS_POLL_MS, type PlatformStatusBannerData } from '../lib/platform-status'

function sameBanner(a: PlatformStatusBannerData | null, b: PlatformStatusBannerData | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.since === b.since && a.message === b.message && a.components.join(',') === b.components.join(',')
}

/**
 * The open platform-status banner, or null. Reads on mount, every two minutes and whenever the window
 * gets focus back. A failed read is "no banner". This is a plain poll, not `useCustomerQuery`: the
 * route is public, shows on signed-out pages too, and has nothing to cache per user.
 */
export function usePlatformStatus(): PlatformStatusBannerData | null {
  const [banner, setBanner] = useState<PlatformStatusBannerData | null>(null)

  useEffect(() => {
    let active = true
    let controller: AbortController | null = null

    const read = () => {
      controller?.abort()
      const current = new AbortController()
      controller = current
      void fetchPlatformStatus(current.signal).then((next) => {
        if (!active || current.signal.aborted) return
        setBanner((previous) => (sameBanner(previous, next) ? previous : next))
      })
    }

    read()
    const timer = window.setInterval(read, PLATFORM_STATUS_POLL_MS)
    window.addEventListener('focus', read)
    return () => {
      active = false
      controller?.abort()
      window.clearInterval(timer)
      window.removeEventListener('focus', read)
    }
  }, [])

  return banner
}
